import { useState, useEffect } from 'react';
import { insuranceSettingsService, InsuranceSetting } from '~/services/insurance-settings.service';
import { message } from 'antd';

export const useInsuranceSettings = () => {
  const [settings, setSettings] = useState<InsuranceSetting[]>([]);
  const [localValues, setLocalValues] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await insuranceSettingsService.getAll();
      setSettings(data);
      // Initialize local values
      const initialValues: Record<string, number> = {};
      data.forEach(setting => {
        initialValues[setting.key] = setting.value;
      });
      setLocalValues(initialValues);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error loading insurance settings:', error);
      message.error('Failed to load insurance settings');
    } finally {
      setLoading(false);
    }
  };

  const updateLocalValue = (key: string, value: number) => {
    setLocalValues(prev => ({
      ...prev,
      [key]: value
    }));
    setHasUnsavedChanges(true);
  };

  const saveAllSettings = async () => {
    try {
      setSaving(true);
      const updates = Object.entries(localValues).map(async ([key, value]) => {
        const originalSetting = settings.find(s => s.key === key);
        if (originalSetting && originalSetting.value !== value) {
          return insuranceSettingsService.updateByKey(key, value);
        }
        return originalSetting;
      });
      
      const updatedSettings = await Promise.all(updates);
      const validUpdatedSettings = updatedSettings.filter(Boolean) as InsuranceSetting[];
      
      setSettings(validUpdatedSettings);
      setHasUnsavedChanges(false);
      message.success('All settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetChanges = () => {
    const originalValues: Record<string, number> = {};
    settings.forEach(setting => {
      originalValues[setting.key] = setting.value;
    });
    setLocalValues(originalValues);
    setHasUnsavedChanges(false);
  };

  const getSettingByKey = (key: string) => {
    return settings.find(setting => setting.key === key);
  };

  const getSettingValue = (key: string): number => {
    return localValues[key] || 0;
  };

  useEffect(() => {
    loadSettings();
  }, []);

  return {
    settings,
    loading,
    saving,
    hasUnsavedChanges,
    updateLocalValue,
    saveAllSettings,
    resetChanges,
    getSettingByKey,
    getSettingValue,
    refreshSettings: loadSettings,
  };
};
